import { useCallback, useEffect } from 'react';

import { useLoadDataRequest } from '@futrli/shared-services';
import { V2FinancialDatasetRequest } from '@futrli/predict/report-builder/utils-queries';

import { getReportBulkLiveData } from '../../../../../core/api/reporting/report-builder';
import { getForecastPeriodCount } from './utils';

type RequestDataType = Awaited<ReturnType<typeof getReportBulkLiveData>>;

/**
 * Loads the daily cash-flow graph numbers for the given organisation / consolidation
 * @param workspaceId - Workspace ID for the consolidation
 * @param organisation - Organisation / consolidation ID and required flags
 * @param forecastId - Forecast ID
 * @param forecastFromDate - Forecast start date (as string YYYY-MM-DD)
 * @param forecastDuration - Forecast duration (in years)
 * @param bankAccountRef - Bank account ref
 */
export const useLoadDailyCashFlowNumbers = (
  workspaceId: string,
  organisation: { id: string; isConsolidation: boolean },
  forecastId?: string,
  forecastFromDate?: string | null,
  forecastDuration?: number,
  bankAccountRef?: string
) => {
  const hasAllParams =
    !!workspaceId && !!organisation && !!forecastId && !!forecastFromDate && !!forecastDuration && !!bankAccountRef;
  const { load, data, ...rest } = useLoadDataRequest<RequestDataType>();

  const reload = useCallback(async () => {
    if (hasAllParams) {
      const queries: Record<string, V2FinancialDatasetRequest> = {
        cashflow: {
          name: 'cashflow',
          forecast_id: forecastId,
          fields: [{ ref: bankAccountRef }],
          facet: null,
          period_type: 'daily',
          period_count: getForecastPeriodCount(forecastFromDate, forecastDuration),
          start_date: forecastFromDate,
          period_offset: 0,
          extra_columns: [],
          actuals_only: false,
          include_assumptions: false,
          include_zero_rows: true
        }
      };
      load(
        getReportBulkLiveData(
          workspaceId,
          organisation.id,
          queries,
          organisation.isConsolidation
        )
      );
    }
  }, [
    bankAccountRef,
    forecastDuration,
    forecastFromDate,
    forecastId,
    hasAllParams,
    load,
    organisation.id,
    organisation.isConsolidation,
    workspaceId
  ]);

  useEffect(() => {
    reload();
  }, [reload]);

  return {
    hasAllParams,
    reload,
    data: data?.cashflow,
    ...rest
  };
};
